import { useTranslation } from "react-i18next";
import { useSearch } from "@tanstack/react-router";
import { ettsBootstrap } from "@/lib/bootstrap";

/**
 * Login stránka — HTML form POST na `/admin/login` (web security chain, CSRF).
 * Plná navigace: Spring autentizuje, nastaví `etts_access` cookie a přesměruje
 * na `/app`. Failure → `/app/login?error` (viz SecurityConfig formLogin).
 *
 * Native `<form>` + native inputs (ne react-aria) — aby se `username`/`password`
 * spolehlivě odeslaly klasickým submitem bez JS.
 */
export function LoginPage() {
  const { t } = useTranslation();
  const search = useSearch({ strict: false }) as { error?: unknown; logout?: unknown; expired?: unknown };
  const csrf = ettsBootstrap();
  const hasError = "error" in search;
  const loggedOut = "logout" in search;
  const expired = "expired" in search;

  return (
    <div className="flex min-h-screen items-center justify-center bg-secondary p-4">
      <div className="w-full max-w-90 rounded-2xl bg-primary p-6 shadow-lg ring-1 ring-secondary sm:p-8">
        <h1 className="text-display-xs font-semibold text-primary">{t("login.title")}</h1>
        <p className="mt-1 text-sm text-tertiary">{t("login.subtitle")}</p>

        {hasError && (
          <div className="mt-4 rounded-lg bg-error-primary px-3 py-2 text-sm text-error-primary ring-1 ring-error">
            {t("login.error")}
          </div>
        )}
        {expired && !hasError && (
          <div className="mt-4 rounded-lg bg-warning-primary px-3 py-2 text-sm text-warning-primary ring-1 ring-warning">
            {t("login.expired")}
          </div>
        )}
        {loggedOut && !hasError && !expired && (
          <div className="mt-4 rounded-lg bg-success-primary px-3 py-2 text-sm text-success-primary ring-1 ring-success">
            {t("login.loggedOut")}
          </div>
        )}

        <form method="post" action="/admin/login" className="mt-6 flex flex-col gap-4">
          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-secondary">{t("login.username")}</span>
            <input
              name="username"
              type="text"
              required
              autoFocus
              autoComplete="username"
              className="rounded-lg bg-primary px-3 py-2.5 text-md text-primary shadow-xs ring-1 ring-primary outline-none focus:ring-2 focus:ring-brand"
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-secondary">{t("login.password")}</span>
            <input
              name="password"
              type="password"
              required
              autoComplete="current-password"
              className="rounded-lg bg-primary px-3 py-2.5 text-md text-primary shadow-xs ring-1 ring-primary outline-none focus:ring-2 focus:ring-brand"
            />
          </label>
          {csrf.csrfToken && <input type="hidden" name="_csrf" value={csrf.csrfToken} />}
          <button
            type="submit"
            className="mt-2 min-h-touch rounded-lg bg-brand-solid px-4 py-2.5 text-md font-semibold text-white shadow-xs hover:bg-brand-solid_hover focus:ring-2 focus:ring-brand"
          >
            {t("login.submit")}
          </button>
        </form>
      </div>
    </div>
  );
}
